'use client';
import { useEffect, useState } from 'react';
import Icon from '../Icon';

interface Props { navigate: (s: string) => void; alertId?: number; }

interface RecItem {
  id: number;
  title: string;
  org: string;
  region: string;
  type: string;
  supportAmount: string | null;
  match: number;
  reason: string[];
  dday: string;
  daysLeft: number | null;
}

// 마감 임박도에 따른 D-day 뱃지 색상.
function ddColors(daysLeft: number | null): { bg: string; color: string } {
  if (daysLeft == null) return { bg: '#e9edff', color: '#003d9b' }; // 상시
  if (daysLeft <= 3) return { bg: '#ffdad6', color: '#93000a' };
  if (daysLeft <= 7) return { bg: '#fde68a', color: '#854d0e' };
  return { bg: '#d6f0e2', color: '#10663f' };
}

const mono = "'JetBrains Mono',monospace";

export default function RecommendScreen({ navigate, alertId }: Props) {
  const [items, setItems] = useState<RecItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [basisName, setBasisName] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setItems(null); setErr(null);
    (async () => {
      try {
        const res = await fetch(`/api/my/recommend${alertId ? `?alertId=${alertId}` : ''}`);
        // 미인증(미들웨어가 /login 으로 리다이렉트)만 로그인 안내. 그 외(500 등)는 일반 오류로 구분.
        if (res.status === 401 || (res.redirected && res.url.includes('/login'))) {
          if (alive) setErr('로그인이 필요합니다.'); return;
        }
        let data: { items?: RecItem[]; error?: string; basis?: { name?: string } } = {};
        try { data = await res.json(); } catch { if (alive) setErr('추천을 불러오지 못했습니다. (서버 재시작이 필요할 수 있습니다)'); return; }
        if (!res.ok) { if (alive) setErr(data.error ?? '추천을 불러오지 못했습니다.'); return; }
        if (alive) { setItems(data.items ?? []); setBasisName(data.basis?.name ?? null); }
      } catch {
        if (alive) setErr('추천을 불러오지 못했습니다.');
      }
    })();
    return () => { alive = false; };
  }, [alertId]);

  const header = (
    <div style={{ marginBottom: 24 }}>
      <h1 className="vh1" style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-.015em', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 9 }}>
        <Icon name="auto_awesome" size={30} color="#00687b" fill={1} />맞춤 추천
      </h1>
      <p style={{ fontSize: 16, color: '#434654', margin: 0 }}>
        지난주 수집된 지원사업 공고문 중 {basisName ? <>관심분야 「<b>{basisName}</b>」</> : '기업 프로필'}과 일치율이 높은 순서로 정렬했습니다.
      </p>
    </div>
  );

  if (err) return (
    <div>{header}
      <div style={{ background: '#fff', border: '1px solid #e4e8f6', borderRadius: 16, padding: 48, textAlign: 'center', color: '#434654' }}>
        <Icon name="lock" size={36} color="#737685" fill={1} />
        <p style={{ margin: '12px 0 16px', fontSize: 15 }}>{err}</p>
        <button onClick={() => navigate('home')} style={{ background: '#003d9b', color: '#fff', border: 'none', cursor: 'pointer', padding: '10px 20px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>홈으로</button>
      </div>
    </div>
  );

  if (items == null) return (
    <div>{header}
      <div style={{ padding: 60, textAlign: 'center', color: '#737685', fontFamily: mono, fontSize: 14 }}>추천 계산 중…</div>
    </div>
  );

  if (items.length === 0) return (
    <div>{header}
      <div style={{ background: '#f1f3ff', borderRadius: 16, padding: 48, textAlign: 'center' }}>
        <Icon name="search_off" size={40} color="#003d9b" />
        <p style={{ margin: '12px 0 6px', fontSize: 16, fontWeight: 700 }}>이번 주 프로필에 맞는 추천이 없어요</p>
        <p style={{ margin: '0 0 16px', fontSize: 14, color: '#434654' }}>프로필을 업데이트하면 더 정확하게 매칭됩니다.</p>
        <button onClick={() => navigate('onboarding')} style={{ background: '#003d9b', color: '#fff', border: 'none', cursor: 'pointer', padding: '11px 20px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>프로필 설정</button>
      </div>
    </div>
  );

  const top = items[0];
  const rest = items.slice(1);
  const topDd = ddColors(top.daysLeft);

  return (
    <div>{header}

      {/* 1위 추천 배너 */}
      <div style={{ background: '#003d9b', borderRadius: 18, padding: 26, color: '#fff', position: 'relative', overflow: 'hidden', marginBottom: 28 }}>
        <div style={{ position: 'absolute', inset: 0, opacity: .1, backgroundImage: 'linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)', backgroundSize: '38px 38px' }} />
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 20, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 260, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,.16)', width: 'max-content', padding: '5px 12px', borderRadius: 999, fontFamily: mono, fontSize: 12, fontWeight: 600 }}>
              <Icon name="workspace_premium" size={15} color="#50dcff" />가장 잘 맞는 공고
            </span>
            <h2 style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.3, margin: 0 }}>{top.title}</h2>
            <span style={{ fontFamily: mono, fontSize: 13, color: '#dae2ff' }}>{top.org}{top.region ? ` · ${top.region}` : ''}</span>
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 2 }}>
              {top.reason.map(r => (
                <span key={r} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(255,255,255,.16)', color: '#fff', fontFamily: mono, fontSize: 12, padding: '4px 10px', borderRadius: 999 }}>
                  <Icon name="check" size={13} color="#50dcff" />{r}
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <a href={`/programs/${top.id}`} style={{ background: '#fff', color: '#003d9b', textDecoration: 'none', padding: '11px 22px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>상세 보기</a>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <div style={{ width: 96, height: 96, borderRadius: '50%', background: '#50dcff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#003d9b' }}>
              <span style={{ fontSize: 30, fontWeight: 700, lineHeight: 1 }}>{top.match}%</span>
              <span style={{ fontFamily: mono, fontSize: 11, fontWeight: 600 }}>일치</span>
            </div>
            <span style={{ fontFamily: mono, fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 6, background: topDd.bg, color: topDd.color }}>{top.dday}</span>
          </div>
        </div>
      </div>

      {/* 나머지 랭킹 리스트 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {rest.map((p, i) => {
          const dd = ddColors(p.daysLeft);
          return (
            <div key={p.id} className="vcol-sm" style={{ background: '#fff', border: '1px solid #e4e8f6', borderRadius: 14, padding: 18, display: 'flex', gap: 16, alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flexShrink: 0, width: 64 }}>
                <span style={{ fontFamily: mono, fontSize: 11, color: '#737685' }}>RANK</span>
                <span style={{ fontSize: 24, fontWeight: 700, color: '#003d9b' }}>{i + 2}</span>
              </div>
              <div style={{ flex: 1, minWidth: 220, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: mono, fontSize: 12, fontWeight: 600, color: '#005f71', background: '#50dcff', padding: '3px 10px', borderRadius: 999 }}>{p.match}% 일치</span>
                  {p.type && <span style={{ fontFamily: mono, fontSize: 11, padding: '3px 8px', borderRadius: 6, background: '#e9edff', color: '#003d9b' }}>{p.type}</span>}
                  <span style={{ fontFamily: mono, fontSize: 12, fontWeight: 600, padding: '3px 9px', borderRadius: 6, background: dd.bg, color: dd.color }}>{p.dday}</span>
                </div>
                <a href={`/programs/${p.id}`} style={{ fontSize: 17, fontWeight: 600, lineHeight: 1.3, margin: 0, color: '#051a3e', textDecoration: 'none' }}>{p.title}</a>
                <span style={{ fontFamily: mono, fontSize: 12, color: '#003d9b' }}>{p.org}{p.region ? ` · ${p.region}` : ''}</span>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {p.reason.map(r => (
                    <span key={r} style={{ display: 'flex', alignItems: 'center', gap: 3, fontFamily: mono, fontSize: 11, background: '#f1f3ff', color: '#10663f', padding: '3px 8px', borderRadius: 6 }}>
                      <Icon name="check" size={13} />{r}
                    </span>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10, flexShrink: 0 }}>
                {p.supportAmount && <span style={{ fontSize: 15, fontWeight: 700, color: '#051a3e', whiteSpace: 'nowrap', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.supportAmount}</span>}
                <a href={`/programs/${p.id}`} style={{ background: '#003d9b', color: '#fff', textDecoration: 'none', padding: '9px 16px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>상세</a>
              </div>
            </div>
          );
        })}
      </div>

      {/* 프로필 업데이트 유도 */}
      <div style={{ marginTop: 20, background: '#f1f3ff', borderRadius: 16, padding: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Icon name="tune" size={26} color="#003d9b" />
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>추천이 더 정확해질 수 있어요</div>
            <div style={{ fontSize: 13, color: '#434654' }}>기업 프로필(업종·기업형태·관심분야·소재지·보유 인증)을 최신으로 유지하면 매칭이 정교해집니다.</div>
          </div>
        </div>
        <button onClick={() => navigate('onboarding')} style={{ background: '#003d9b', color: '#fff', border: 'none', cursor: 'pointer', padding: '11px 20px', borderRadius: 10, fontSize: 14, fontWeight: 600, flexShrink: 0 }}>프로필 설정</button>
      </div>
    </div>
  );
}
